import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../Button';
import {
    parseRequesterEmails,
    suggestRequesterEmails,
    type RequesterEmailSource,
} from '../../utils/quickCreatePrefs';

export type EmailSuggestFieldProps = {
    value: string;
    onChange: (value: string) => void;
    /** Called with a validated or suggested email to add. */
    onCommit: (email: string) => void;
    sources: RequesterEmailSource[];
    excludedEmails?: string[];
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    disabled?: boolean;
    showAddButton?: boolean;
    autoFocus?: boolean;
    /** When true, comma/semicolon also commits a parsed email list. */
    allowMulti?: boolean;
};

/**
 * Free-text email input with keyboard-navigable suggestions from known contacts.
 */
function EmailSuggestField(props: EmailSuggestFieldProps) {
    const {
        value,
        onChange,
        onCommit,
        sources,
        excludedEmails = [],
        placeholder = '',
        className = '',
        inputClassName = 'w-full rounded border-gray-300 dark:border-gray-600 text-sm',
        disabled = false,
        showAddButton = true,
        autoFocus = false,
        allowMulti = false,
    } = props;

    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const pickingSuggestion = useRef(false);

    const suggestions = useMemo(() => {
        if (!open)
            return [];
        return suggestRequesterEmails(value, sources, excludedEmails);
    }, [open, value, sources, excludedEmails]);

    useEffect(() => {
        if (highlight >= suggestions.length)
            setHighlight(0);
    }, [suggestions.length, highlight]);

    const commitSuggestion = (email: string) => {
        onCommit(email);
        onChange('');
        setOpen(false);
        setHighlight(0);
        pickingSuggestion.current = false;
    };

    const commitTyped = (raw = value) => {
        const trimmed = raw.trim();
        if (!trimmed)
            return;
        if (allowMulti) {
            const parsed = parseRequesterEmails(trimmed);
            if (!parsed.length) {
                onCommit(trimmed);
                return;
            }
            parsed.forEach((email) => onCommit(email));
            onChange('');
            setOpen(false);
            setHighlight(0);
            return;
        }
        onCommit(trimmed);
    };

    return (
        <div className={`relative ${className}`.trim()}>
            <div className="flex gap-2">
                <input
                    className={inputClassName}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    onChange={(e) => {
                        onChange(e.target.value);
                        setOpen(true);
                        setHighlight(0);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' && suggestions.length) {
                            e.preventDefault();
                            setOpen(true);
                            setHighlight((index) => (index + 1) % suggestions.length);
                            return;
                        }
                        if (e.key === 'ArrowUp' && suggestions.length) {
                            e.preventDefault();
                            setOpen(true);
                            setHighlight((index) =>
                                (index - 1 + suggestions.length) % suggestions.length
                            );
                            return;
                        }
                        if (e.key === 'Escape') {
                            setOpen(false);
                            return;
                        }
                        if (allowMulti && (e.key === ',' || e.key === ';')) {
                            e.preventDefault();
                            commitTyped(value + e.key);
                            return;
                        }
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (open && suggestions[highlight]) {
                                commitSuggestion(suggestions[highlight].email);
                                return;
                            }
                            commitTyped();
                        }
                    }}
                    onBlur={() => {
                        window.setTimeout(() => {
                            if (pickingSuggestion.current)
                                return;
                            setOpen(false);
                            if (allowMulti && value.trim())
                                commitTyped();
                        }, 120);
                    }}
                />
                {showAddButton && (
                    <Button
                        title=""
                        icon="plus"
                        color="secondary"
                        small
                        disabled={disabled}
                        onClick={commitTyped}
                    />
                )}
            </div>
            {open && suggestions.length > 0 && (
                <div className="absolute z-30 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={suggestion.email}
                            type="button"
                            className={`block w-full text-left px-3 py-2 text-sm ${
                                index === highlight
                                    ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-800'
                                    : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900'
                            }`}
                            onMouseDown={() => {
                                pickingSuggestion.current = true;
                            }}
                            onMouseEnter={() => setHighlight(index)}
                            onClick={() => commitSuggestion(suggestion.email)}
                        >
                            {suggestion.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default EmailSuggestField;
